import {
  clearTemporaryToken,
  getBearerToken,
  getTemporaryToken,
} from '@/features/auth/auth.utils.js'
import { isInIframe } from '@/features/embed/isInIframe.js'
import { postMessageEvents, sendParentPostMessage } from '@/features/embed/postMessage.js'
import { getActiveClubId } from '@/features/router/useActiveClub.js'

function goToUrl(url) {
  window.location.href = url
}

function getCurrentUrl() {
  return `${window.location.pathname}${window.location.search}`
}

function isPublicAuthFlowPath() {
  const path = window.location.pathname

  return (
    path.includes('/auth/login') ||
    path.includes('/auth/register') ||
    path.includes('/auth/forgot-password')
  )
}

function buildAuthRoute(path, redirectUrl, extraParams = {}) {
  const params = new URLSearchParams()
  const activeClubId = getActiveClubId()

  if (activeClubId) {
    params.set('activeClubId', activeClubId)
  }

  params.set('redirectUrl', redirectUrl)
  for (const [key, value] of Object.entries(extraParams)) {
    if (value != null && value !== '') {
      params.set(key, value)
    }
  }

  return `${path}?${params.toString()}`
}

const temporaryTokenRetryAtKey = 'temporaryTokenRetryAt'
const temporaryTokenRetryWindowMs = 30000

// 401 with the temporary tenant token only means the temporary token expired
// or was invalidated — the main session may still be valid. Clear it and
// reload once so tenantTokenGuard re-issues a fresh token via LoginTenant.
function handleTemporaryTokenUnauthorized(error) {
  const temporaryToken = getTemporaryToken()

  if (!temporaryToken) {
    return false
  }

  const authHeader = error?.config?.headers?.Authorization

  if (authHeader !== `Bearer ${temporaryToken}`) {
    return false
  }

  clearTemporaryToken()

  const lastRetryAt = Number(sessionStorage.getItem(temporaryTokenRetryAtKey) || 0)

  if (Date.now() - lastRetryAt < temporaryTokenRetryWindowMs) {
    // Already retried recently — fall through to the session-expired flow.
    return false
  }

  sessionStorage.setItem(temporaryTokenRetryAtKey, String(Date.now()))
  console.log('[unauthorizedHandler] Temporary tenant token rejected, reloading to re-login')
  window.location.reload()
  return true
}

export function unauthorizedHandler(error) {
  console.log('[unauthorizedHandler] Checking error:', {
    status: error?.response?.status,
    message: error?.message,
    code: error?.code,
  })

  if (error?.response?.status === 401) {
    console.log('[unauthorizedHandler] 401 detected, handling...')
    if (isInIframe()) {
      doParentLogout()
      return Promise.reject(error)
    }

    if (handleTemporaryTokenUnauthorized(error)) {
      return Promise.reject(error)
    }

    const token = getBearerToken()
    const redirectUrl = getCurrentUrl()

    if (token) {
      console.log('[unauthorizedHandler] Token found, redirecting to /auth/logout')
      goToUrl(buildAuthRoute('/auth/logout', redirectUrl, { logoutFlow: 'sessionExpired' }))
      return Promise.reject(error)
    }

    if (!isPublicAuthFlowPath()) {
      console.log('[unauthorizedHandler] No token, redirecting to /auth/login')
      goToUrl(buildAuthRoute('/auth/login', redirectUrl))
    }

    return Promise.reject(error)
  }

  console.log('[unauthorizedHandler] Not 401, re-throwing error')
  throw error
}

export function unauthorizedResponseInterceptor(client) {
  client.interceptors.response.use(request => request, unauthorizedHandler)
}

export function doParentLogout() {
  console.log('[unauthorizedHandler] Sending post message FM_doLogout, parent should logout')
  sendParentPostMessage({ type: postMessageEvents.FM_doLogout })
}
